import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api, type ApiError, type ListQuoteTemplatesResult } from '@whinerp/client';
import { Button, Skeleton } from '@whinerp/ui';
import { useErrorMessage, useT } from '../../app/i18n.js';
import { useEntitlement } from '../../app/session.js';

/**
 * Starting a quote: blank, or from a template (F-158).
 *
 * The UI pointer: "Template picker appears as the first step of quote
 * creation, with a blank option." Blank comes first because it is the answer
 * that always works.
 *
 * Only templates in the deal's currency are offered. A template in another
 * currency is refused when applied, and offering it would be offering a
 * refusal.
 *
 * With templates switched off there is no step to show, and the button starts
 * a blank quote as it always did. It used to do that and throw away any
 * refusal, so a quote that could not be started did nothing visible at all.
 */
export function NewQuoteButton({ dealId, currency }: { dealId: string; currency: string }): JSX.Element {
  const t = useT();
  const errorText = useErrorMessage();
  const navigate = useNavigate();
  const templating = useEntitlement('F-158');
  const [picking, setPicking] = useState(false);

  const templates = useQuery<ListQuoteTemplatesResult, ApiError>({
    queryKey: ['quote-templates', currency],
    queryFn: () => api.listQuoteTemplates({ query: { currency } }),
    enabled: picking && templating.enabled,
  });

  const start = useMutation<{ quoteId: string }, ApiError, string | null>({
    mutationFn: (templateId) =>
      api.createQuote({ path: { dealId }, body: templateId ? { templateId } : {} }),
    onSuccess: (created) => navigate(`/deals/${dealId}/quotes/${created.quoteId}`),
  });

  return (
    <div className="flex flex-col gap-2">
      <Button
        intent="secondary"
        icon="FilePlus"
        data-new-quote
        disabled={start.isPending}
        onClick={() => (templating.enabled ? setPicking((open) => !open) : start.mutate(null))}
      >
        {t('quote.new')}
      </Button>

      {start.error ? (
        <p data-new-quote-refusal className="rounded-control bg-danger-tint px-3 py-2 text-dense text-danger-text">
          {errorText(start.error)}
        </p>
      ) : null}

      {picking && templating.enabled ? (
        <div data-quote-template-picker className="flex flex-col gap-1 rounded-card border border-rule p-2">
          <span className="text-meta text-ink-meta">{t('quoteTemplate.pickerTitle')}</span>
          <Button intent="ghost" size="small" data-quote-template-choice="blank" onClick={() => start.mutate(null)}>
            {t('quoteTemplate.blank')}
          </Button>
          {templates.isPending ? (
            <Skeleton className="h-input w-full" />
          ) : templates.isError ? (
            <p className="text-dense text-danger-text">{errorText(templates.error)}</p>
          ) : templates.data.templates.length === 0 ? (
            <p data-quote-template-none className="text-meta text-ink-meta">
              {t('quoteTemplate.noneInCurrency', { values: { currency } })}
            </p>
          ) : (
            templates.data.templates.map((template) => (
              <Button
                key={template.templateId}
                intent="ghost"
                size="small"
                data-quote-template-choice={template.templateId}
                onClick={() => start.mutate(template.templateId)}
              >
                {t('quoteTemplate.choice', {
                  values: { name: template.name, lines: String(template.lineCount) },
                })}
              </Button>
            ))
          )}
        </div>
      ) : null}
    </div>
  );
}
